/**
 * ManageLogging.jsx
 * 
 * Super Admin page for managing logging configuration and viewing application logs.
 * 
 * FEATURES:
 * 1. Configuration Management:
 *    - Toggle logging subjects ON/OFF
 *    - Adjust performance thresholds
 *    - View current performance metrics
 * 
 * 2. Log Viewer:
 *    - Search logs by message content
 *    - Filter by subject, level, time range
 *    - Trace by correlation ID or session ID
 *    - Real-time log statistics
 * 
 * REQUIRES: Super Admin role
 * 
 * Created: January 14, 2026
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import {
  Settings,
  Search,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Info,
  AlertTriangle,
  XCircle,
  CheckCircle,
  Clock,
  Activity,
  Eye,
  EyeOff,
  BarChart3,
  Layers,
  Terminal,
  Shield,
  Zap,
  Globe,
  Users,
  FileText
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// SUBJECT METADATA
// =============================================================================
const SUBJECT_INFO = {
  api_routing: {
    label: 'API Routing',
    description: 'REST API URL and entry point (light)',
    icon: Globe,
    color: 'blue'
  },
  functional_flow: {
    label: 'Functional Flow',
    description: 'All function calls in request path (verbose)',
    icon: Layers,
    color: 'purple'
  },
  error_logging: {
    label: 'Error Logging',
    description: 'Exceptions with stack traces',
    icon: XCircle,
    color: 'red'
  },
  operation_audit: {
    label: 'Operation Audit',
    description: 'User actions (CRUD, login, settings)',
    icon: Users,
    color: 'green'
  },
  external_service: {
    label: 'External Service',
    description: 'External API calls (Twilio, Stripe)',
    icon: Zap,
    color: 'orange'
  },
  performance: {
    label: 'Performance',
    description: 'Request timing, slow detection',
    icon: Activity,
    color: 'yellow'
  },
  security: {
    label: 'Security',
    description: 'Failed logins, suspicious activity',
    icon: Shield,
    color: 'red'
  },
  frontend: {
    label: 'Frontend',
    description: 'Frontend logs via API',
    icon: Terminal,
    color: 'cyan'
  }
};

// =============================================================================
// LEVEL BADGE COMPONENT
// =============================================================================
const LevelBadge = ({ level }) => {
  const styles = {
    INFO: 'bg-blue-100 text-blue-800',
    WARNING: 'bg-yellow-100 text-yellow-800',
    ERROR: 'bg-red-100 text-red-800',
    DEBUG: 'bg-gray-100 text-gray-800',
    CRITICAL: 'bg-red-200 text-red-900'
  };

  const icons = {
    INFO: Info,
    WARNING: AlertTriangle,
    ERROR: XCircle,
    DEBUG: Terminal,
    CRITICAL: AlertCircle
  };

  const Icon = icons[level] || Info;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[level] || styles.INFO}`}>
      <Icon className="w-3 h-3" />
      {level}
    </span>
  );
};

// =============================================================================
// SUBJECT BADGE COMPONENT
// =============================================================================
const SubjectBadge = ({ subject }) => {
  const info = SUBJECT_INFO[subject] || { label: subject, color: 'gray' };
  const colorStyles = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200'
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${colorStyles[info.color]}`}>
      {info.label}
    </span>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const ManageLogging = () => {
  const navigate = useNavigate();
  const { runningUser } = useAuth();
  const token = localStorage.getItem('token');

  // Configuration state
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);

  // Log viewer state
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logStats, setLogStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0, hasMore: false });

  // Filters state
  const [filters, setFilters] = useState({
    subject: '',
    level: '',
    search: '',
    hours: 24,
    correlationId: '',
    sessionId: '',
    userId: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Expanded log state
  const [expandedLogIndex, setExpandedLogIndex] = useState(null);

  // Active tab
  const [activeTab, setActiveTab] = useState('config'); // 'config' or 'viewer'

  // ==========================================================================
  // CHECK ACCESS
  // ==========================================================================
  useEffect(() => {
    if (runningUser?.role !== 'super_admin') {
      navigate('/dashboard');
    }
  }, [runningUser, navigate]);

  // ==========================================================================
  // LOAD CONFIG
  // ==========================================================================
  const loadConfig = useCallback(async () => {
    try {
      setConfigLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/logs/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to load logging config:', error);
    } finally {
      setConfigLoading(false);
    }
  }, [token]);

  // ==========================================================================
  // LOAD LOGS
  // ==========================================================================
  const loadLogs = useCallback(async (page = 1) => {
    try {
      setLogsLoading(true);
      
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pagination.pageSize);
      
      if (filters.subject) params.append('subject', filters.subject);
      if (filters.level) params.append('level', filters.level);
      if (filters.search) params.append('search', filters.search);
      if (filters.hours) params.append('hours', filters.hours);
      if (filters.correlationId) params.append('correlation_id', filters.correlationId);
      if (filters.sessionId) params.append('session_id', filters.sessionId);
      if (filters.userId) params.append('user_id', filters.userId);

      const response = await axios.get(`${API_BASE_URL}/api/logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLogs(response.data.logs);
      setPagination({
        ...pagination,
        page: response.data.page,
        total: response.data.total,
        hasMore: response.data.has_more
      });
    } catch (error) {
      console.error('Failed to load logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, [token, filters, pagination.pageSize]);

  // ==========================================================================
  // LOAD STATS
  // ==========================================================================
  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/logs/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogStats(response.data);
    } catch (error) {
      console.error('Failed to load log stats:', error);
    }
  }, [token]);

  // ==========================================================================
  // INITIAL LOAD
  // ==========================================================================
  useEffect(() => {
    loadConfig();
    loadStats();
  }, [loadConfig, loadStats]);

  useEffect(() => {
    if (activeTab === 'viewer') {
      loadLogs(1);
    }
  }, [activeTab]);

  // ==========================================================================
  // TOGGLE SUBJECT
  // ==========================================================================
  const toggleSubject = async (subject) => {
    const newValue = !config.subjects[subject];
    
    // Optimistic update
    setConfig(prev => ({
      ...prev,
      subjects: { ...prev.subjects, [subject]: newValue }
    }));

    try {
      await axios.put(
        `${API_BASE_URL}/api/logs/config`,
        { subjects: { [subject]: newValue } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      // Revert on error
      setConfig(prev => ({
        ...prev,
        subjects: { ...prev.subjects, [subject]: !newValue }
      }));
      console.error('Failed to update subject:', error);
    }
  };

  // ==========================================================================
  // UPDATE THRESHOLD
  // ==========================================================================
  const updateThreshold = async (newThreshold) => {
    try {
      setSavingConfig(true);
      await axios.put(
        `${API_BASE_URL}/api/logs/config`,
        { performance_threshold_ms: parseInt(newThreshold) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfig(prev => ({ ...prev, performance_threshold_ms: parseInt(newThreshold) }));
    } catch (error) {
      console.error('Failed to update threshold:', error);
    } finally {
      setSavingConfig(false);
    }
  };

  // ==========================================================================
  // APPLY FILTERS
  // ==========================================================================
  const applyFilters = () => {
    loadLogs(1);
  };

  // ==========================================================================
  // CLEAR FILTERS
  // ==========================================================================
  const clearFilters = () => {
    setFilters({
      subject: '',
      level: '',
      search: '',
      hours: 24,
      correlationId: '',
      sessionId: '',
      userId: ''
    });
  };

  // ==========================================================================
  // FORMAT TIMESTAMP
  // ==========================================================================
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  if (runningUser?.role !== 'super_admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="w-8 h-8 text-yellow-500" />
            Logging Management
          </h1>
          <p className="text-gray-600 mt-2">
            Configure logging subjects and view application logs
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('config')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'config'
                ? 'bg-yellow-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Settings className="w-4 h-4 inline-block mr-2" />
            Configuration
          </button>
          <button
            onClick={() => setActiveTab('viewer')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'viewer'
                ? 'bg-yellow-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Search className="w-4 h-4 inline-block mr-2" />
            Log Viewer
          </button>
        </div>

        {/* Configuration Tab */}
        {activeTab === 'config' && (
          <div className="space-y-6">
            {/* Subjects Grid */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Layers className="w-5 h-5 text-yellow-500" />
                Logging Subjects
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Toggle logging subjects ON/OFF. Changes take effect immediately.
              </p>

              {configLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {Object.entries(SUBJECT_INFO).map(([key, info]) => {
                    const Icon = info.icon;
                    const isEnabled = config?.subjects?.[key] ?? false;

                    return (
                      <div
                        key={key}
                        className={`relative p-4 rounded-lg border-2 transition-all cursor-pointer ${
                          isEnabled
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 bg-gray-50 opacity-60'
                        }`}
                        onClick={() => toggleSubject(key)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <Icon className={`w-5 h-5 ${isEnabled ? 'text-green-600' : 'text-gray-400'}`} />
                          <div className={`w-10 h-6 rounded-full transition-colors ${
                            isEnabled ? 'bg-green-500' : 'bg-gray-300'
                          }`}>
                            <div className={`w-4 h-4 mt-1 rounded-full bg-white shadow transition-transform ${
                              isEnabled ? 'translate-x-5' : 'translate-x-1'
                            }`} />
                          </div>
                        </div>
                        <h3 className="font-medium text-gray-900">{info.label}</h3>
                        <p className="text-xs text-gray-500 mt-1">{info.description}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Performance Settings */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-yellow-500" />
                Performance Settings
              </h2>

              {config && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Slow Request Threshold (ms)
                    </label>
                    <input
                      type="number"
                      value={config.performance_threshold_ms}
                      onChange={(e) => setConfig(prev => ({ ...prev, performance_threshold_ms: e.target.value }))}
                      onBlur={(e) => updateThreshold(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Requests slower than this are logged as "SLOW"
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-500 mb-1">Current Average</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {config.current_avg_request_time_ms.toFixed(2)} ms
                    </div>
                    <div className="text-xs text-gray-500">
                      Based on {config.sample_count} samples
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-500 mb-1">Sample Size</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {config.performance_avg_samples}
                    </div>
                    <div className="text-xs text-gray-500">
                      Requests used for average
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Log Statistics */}
            {logStats && (
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-yellow-500" />
                  Log Statistics
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">By Subject</h3>
                    <div className="space-y-2">
                      {Object.entries(logStats.by_subject).map(([subject, count]) => (
                        <div key={subject} className="flex items-center justify-between">
                          <SubjectBadge subject={subject} />
                          <span className="text-sm font-medium text-gray-900">{count.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">By Level</h3>
                    <div className="space-y-2">
                      {Object.entries(logStats.by_level).map(([level, count]) => (
                        <div key={level} className="flex items-center justify-between">
                          <LevelBadge level={level} />
                          <span className="text-sm font-medium text-gray-900">{count.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900">{logStats.total.toLocaleString()}</div>
                    <div className="text-sm text-gray-500">Total Logs (30 day retention)</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Log Viewer Tab */}
        {activeTab === 'viewer' && (
          <div className="space-y-4">
            {/* Search and Filters */}
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex flex-wrap gap-4 items-center">
                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search in messages..."
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    />
                  </div>
                </div>

                {/* Quick Filters */}
                <select
                  value={filters.subject}
                  onChange={(e) => setFilters(prev => ({ ...prev, subject: e.target.value }))}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">All Subjects</option>
                  {Object.entries(SUBJECT_INFO).map(([key, info]) => (
                    <option key={key} value={key}>{info.label}</option>
                  ))}
                </select>

                <select
                  value={filters.level}
                  onChange={(e) => setFilters(prev => ({ ...prev, level: e.target.value }))}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">All Levels</option>
                  <option value="INFO">INFO</option>
                  <option value="WARNING">WARNING</option>
                  <option value="ERROR">ERROR</option>
                  <option value="DEBUG">DEBUG</option>
                </select>

                <select
                  value={filters.hours}
                  onChange={(e) => setFilters(prev => ({ ...prev, hours: parseInt(e.target.value) }))}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="1">Last 1 hour</option>
                  <option value="6">Last 6 hours</option>
                  <option value="24">Last 24 hours</option>
                  <option value="72">Last 3 days</option>
                  <option value="168">Last 7 days</option>
                </select>

                {/* Toggle Advanced Filters */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg flex items-center gap-1"
                >
                  <Filter className="w-4 h-4" />
                  {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {/* Search Button */}
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  Search
                </button>

                {/* Refresh */}
                <button
                  onClick={() => loadLogs(pagination.page)}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  title="Refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${logsLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Correlation ID
                    </label>
                    <input
                      type="text"
                      placeholder="Trace specific request..."
                      value={filters.correlationId}
                      onChange={(e) => setFilters(prev => ({ ...prev, correlationId: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Session ID
                    </label>
                    <input
                      type="text"
                      placeholder="Trace browser session..."
                      value={filters.sessionId}
                      onChange={(e) => setFilters(prev => ({ ...prev, sessionId: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      User ID
                    </label>
                    <input
                      type="text"
                      placeholder="Trace specific user..."
                      value={filters.userId}
                      onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  <div className="md:col-span-3 flex justify-end">
                    <button
                      onClick={clearFilters}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      Clear all filters
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Results Summary */}
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>
                Showing {logs.length} of {pagination.total.toLocaleString()} logs
              </span>
              <span>
                Page {pagination.page}
              </span>
            </div>

            {/* Log List */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              {logsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No logs found matching your criteria</p>
                </div>
              ) : (
                <div className="divide-y">
                  {logs.map((log, index) => (
                    <div
                      key={index}
                      className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                        expandedLogIndex === index ? 'bg-yellow-50' : ''
                      }`}
                      onClick={() => setExpandedLogIndex(expandedLogIndex === index ? null : index)}
                    >
                      {/* Log Header */}
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {expandedLogIndex === index ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <LevelBadge level={log.level} />
                            <SubjectBadge subject={log.subject} />
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTimestamp(log.timestamp)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-900 font-mono truncate">
                            {log.message}
                          </p>
                          {log.user_email && (
                            <p className="text-xs text-gray-500 mt-1">
                              User: {log.user_email} ({log.user_role})
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {expandedLogIndex === index && (
                        <div className="mt-4 ml-7 p-4 bg-gray-50 rounded-lg text-sm">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            {log.correlation_id && (
                              <div>
                                <div className="text-xs text-gray-500">Correlation ID</div>
                                <div className="font-mono text-xs break-all">{log.correlation_id}</div>
                              </div>
                            )}
                            {log.session_id && (
                              <div>
                                <div className="text-xs text-gray-500">Session ID</div>
                                <div className="font-mono text-xs break-all">{log.session_id}</div>
                              </div>
                            )}
                            {log.ip && (
                              <div>
                                <div className="text-xs text-gray-500">IP Address</div>
                                <div className="font-mono text-xs">{log.ip}</div>
                              </div>
                            )}
                            {log.path && (
                              <div>
                                <div className="text-xs text-gray-500">Path</div>
                                <div className="font-mono text-xs">{log.method} {log.path}</div>
                              </div>
                            )}
                          </div>
                          
                          {log.data && (
                            <div>
                              <div className="text-xs text-gray-500 mb-1">Data</div>
                              <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto">
                                {JSON.stringify(log.data, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {logs.length > 0 && (
                <div className="flex items-center justify-between p-4 border-t bg-gray-50">
                  <button
                    onClick={() => loadLogs(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 text-sm border rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {pagination.page}
                  </span>
                  <button
                    onClick={() => loadLogs(pagination.page + 1)}
                    disabled={!pagination.hasMore}
                    className="px-4 py-2 text-sm border rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageLogging;
